<template>
  <section class="pbt" aria-labelledby="public-bookings-timeline-title" :aria-busy="loading">
    <header class="pbt-heading">
      <div>
        <span class="pbt-kicker">Presenze e acquisti</span>
        <h2 id="public-bookings-timeline-title">Elenco prenotazioni</h2>
        <p>Controlla gli appuntamenti in ordine di data e registra chi ha partecipato.</p>
      </div>
      <div class="pbt-heading-actions">
        <button type="button" class="pbt-button staff-button-neutral" @click="$emit('back')">
          <span class="material-icons" aria-hidden="true">calendar_month</span>Vista attuale
        </button>
        <button type="button" class="pbt-button staff-button-neutral" :disabled="loading" @click="$emit('refresh')">
          <span class="material-icons" aria-hidden="true">refresh</span>{{ loading ? 'Caricamento…' : 'Aggiorna' }}
        </button>
      </div>
    </header>

    <div class="pbt-filters">
      <label class="pbt-field pbt-search">
        <span>Cerca cliente</span>
        <input v-model="filters.search" type="search" placeholder="Nome, email o telefono" autocomplete="off" />
      </label>
      <label class="pbt-field"><span>Dal</span><input v-model="filters.from" type="date" :max="filters.to || undefined" /></label>
      <label class="pbt-field"><span>Al</span><input v-model="filters.to" type="date" :min="filters.from || undefined" /></label>
      <label class="pbt-field">
        <span>Partecipato</span>
        <select v-model="filters.attendance">
          <option value="">Tutti</option>
          <option value="yes">Sì</option>
          <option value="no">No</option>
          <option value="unreviewed">Da verificare</option>
        </select>
      </label>
      <label class="pbt-field">
        <span>Ordine per data</span>
        <select v-model="filters.order"><option value="desc">Più recenti prima</option><option value="asc">Meno recenti prima</option></select>
      </label>
      <button v-if="hasFilters" type="button" class="pbt-reset" @click="resetFilters">Rimuovi filtri</button>
    </div>

    <div v-if="!historyLoaded" class="pbt-notice" role="status">
      <span class="material-icons" aria-hidden="true">history</span>
      {{ loading ? 'Caricamento di tutte le prenotazioni, comprese quelle passate…' : 'Lo storico non è ancora completo. Aggiorna per caricare tutte le prenotazioni.' }}
    </div>
    <div v-if="invalidRange" class="pbt-notice" role="alert">La data finale deve essere uguale o successiva a quella iniziale.</div>

    <div class="pbt-summary" aria-live="polite">
      <span><strong>{{ filteredBookings.length }}</strong> prenotazioni{{ hasFilters ? ' trovate' : (historyLoaded ? ' totali' : ' caricate') }}</span>
      <span><strong>{{ attendedCount }}</strong> presenze registrate</span>
      <span class="pbt-summary-help">Gli acquisti vengono verificati per le prenotazioni visibili.</span>
      <label class="pbt-field pbt-page-size">
        <span>Per pagina</span>
        <select v-model.number="pageSize" aria-label="Prenotazioni per pagina">
          <option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
        </select>
      </label>
    </div>

    <div v-if="pagedBookings.length" class="pbt-list">
      <div class="pbt-column-headings" aria-hidden="true">
        <span>Appuntamento</span><span>Cliente</span><span>Situazione</span><span>Note</span><span>Partecipato</span><span>Acquisto dopo l’appuntamento</span><span>Azioni</span>
      </div>
      <article v-for="booking in pagedBookings" :key="booking.id" class="pbt-row" :class="{ 'pbt-row--cancelled': isCancelledBooking(booking) }" :aria-label="`Prenotazione di ${booking.fullName || booking.email || 'cliente'} del ${formatDate(booking.dateKey)}`">
        <div class="pbt-appointment">
          <time class="pbt-date" :class="dateDetails(booking.dateKey).tone" :datetime="booking.dateKey || undefined" :aria-label="formatDate(booking.dateKey)">
            <strong>{{ dateDetails(booking.dateKey).day }}</strong>
            <span v-if="dateDetails(booking.dateKey).year">{{ dateDetails(booking.dateKey).year }}</span>
          </time>
          <span class="pbt-time"><span class="material-icons" aria-hidden="true">schedule</span>{{ booking.timeKey || 'Orario non indicato' }}</span>
          <span class="pbt-mode"><span class="material-icons" aria-hidden="true">{{ booking.meetingMode === 'in_person' ? 'location_on' : 'videocam' }}</span>{{ booking.meetingMode === 'in_person' ? 'In presenza' : 'Online' }}</span>
          <span v-if="isCancelledBooking(booking)" class="pbt-cancelled">Annullata</span>
        </div>
        <div class="pbt-client">
          <h3>{{ booking.fullName || 'Nome non indicato' }}</h3>
          <a v-if="booking.email" :href="'mailto:' + booking.email">{{ booking.email }}</a>
          <a v-if="booking.phone" :href="'tel:' + String(booking.phone).replace(/[^\d+]/g, '')">{{ booking.phone }}</a>
          <span v-if="!booking.email && !booking.phone" class="pbt-muted">Contatti non indicati</span>
        </div>
        <div class="pbt-situation">
          <span class="pbt-mobile-label">Situazione</span>
          <span class="pbt-situation-value" :title="situationDetails(booking).title">
            <span class="pbt-situation-icon material-icons" :class="situationDetails(booking).className" aria-hidden="true">{{ situationDetails(booking).icon }}</span>
            <span>{{ situationDetails(booking).label }}</span>
          </span>
        </div>
        <div class="pbt-notes">
          <span class="pbt-mobile-label">Note</span>
          <PublicBookingNote
            :note="booking.note || ''"
            :client-name="booking.fullName || booking.email || 'cliente'"
            :editable="canManage"
            :disabled="actionsDisabled || loading"
            :save-note="note => saveNote(booking, note)"
          />
        </div>
        <div class="pbt-attendance">
          <PublicBookingAttendance :booking="booking" :saving="!!attendanceSaving[booking.id]" :pending-value="attendancePending[booking.id]" :error="attendanceErrors[booking.id]" :disabled="!canManage || actionsDisabled" @change="$emit('attendance-change', { booking, participated: $event })" />
        </div>
        <div class="pbt-purchase">
          <span class="pbt-mobile-label">Acquisto dopo l’appuntamento</span>
          <span class="pbt-purchase-label" :class="`pbt-purchase-label--${purchaseDetails(booking).tone || 'neutral'}`">{{ purchaseDetails(booking).label }}</span>
          <p v-if="purchaseDetails(booking).detail">{{ purchaseDetails(booking).detail }}</p>
        </div>
        <div class="pbt-actions">
          <button v-if="booking.userId" type="button" class="pbt-button pbt-icon-button staff-button-neutral" title="Apri profilo" :aria-label="`Apri profilo di ${booking.fullName || 'cliente'}`" @click="$emit('open-profile', booking.userId)">
            <span class="material-icons" aria-hidden="true">person</span>
          </button>
          <span v-else class="pbt-muted">Profilo non associato</span>
          <button v-if="canManage && !isCancelledBooking(booking)" type="button" class="pbt-button pbt-icon-button staff-button-neutral" :disabled="actionsDisabled || loading" :title="booking.followUpReminderId ? 'Promemoria follow-up già creato' : 'Crea promemoria follow-up'" :aria-label="`${booking.followUpReminderId ? 'Promemoria follow-up già creato' : 'Crea promemoria follow-up'}: ${booking.fullName || 'cliente'}`" @click="$emit('follow-up', booking)">
            <span class="material-icons" aria-hidden="true">{{ booking.followUpReminderId ? 'notifications_active' : 'alarm_add' }}</span>
          </button>
          <component
            :is="whatsAppChatUrl(booking.phone) ? 'a' : 'button'"
            class="pbt-button pbt-icon-button pbt-whatsapp-button"
            :href="whatsAppChatUrl(booking.phone) || undefined"
            :target="whatsAppChatUrl(booking.phone) ? '_blank' : undefined"
            :rel="whatsAppChatUrl(booking.phone) ? 'noopener noreferrer' : undefined"
            :type="whatsAppChatUrl(booking.phone) ? undefined : 'button'"
            :disabled="whatsAppChatUrl(booking.phone) ? undefined : true"
            :title="whatsAppChatUrl(booking.phone) ? `Apri WhatsApp: ${booking.phone}` : 'Numero WhatsApp non disponibile o non valido'"
            :aria-label="whatsAppChatUrl(booking.phone) ? `Apri WhatsApp di ${booking.fullName || 'cliente'}` : 'Numero WhatsApp non disponibile o non valido'"
          >
            <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><path fill="currentColor" d="M16 3C9.37 3 4 8.37 4 15c0 2.04.52 4.03 1.5 5.8L4 29l8.38-1.47A11.9 11.9 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 3 16 3Zm0 22.2c-1.78 0-3.53-.47-5.05-1.36l-.36-.21-4.05.71.72-3.94-.24-.38A10.12 10.12 0 0 1 5.8 15C5.8 9.37 10.37 4.8 16 4.8S26.2 9.37 26.2 15 21.63 25.2 16 25.2Zm5.62-7.66c-.31-.15-1.83-.9-2.11-1-.28-.1-.49-.15-.69.16-.2.3-.79 1-.97 1.18-.18.2-.36.22-.67.08-.31-.16-1.31-.48-2.49-1.53-.92-.82-1.54-1.84-1.72-2.14-.18-.31-.02-.48.14-.63.14-.14.31-.36.46-.54.15-.18.2-.31.31-.51.1-.2.05-.38-.03-.54-.08-.15-.69-1.66-.95-2.28-.25-.6-.51-.52-.69-.53h-.59c-.2 0-.54.08-.82.39-.28.31-1.08 1.05-1.08 2.56s1.11 2.97 1.26 3.17c.15.2 2.18 3.31 5.28 4.64.74.32 1.31.51 1.76.65.74.23 1.42.2 1.95.12.59-.09 1.83-.74 2.09-1.46.26-.72.26-1.33.18-1.46-.08-.13-.28-.2-.6-.36Z" /></svg>
          </component>
        </div>
      </article>
    </div>
    <div v-else class="pbt-empty" role="status">
      <span class="material-icons" aria-hidden="true">event_note</span>
      <strong>{{ loading ? 'Caricamento prenotazioni…' : 'Nessuna prenotazione trovata' }}</strong>
      <p v-if="!loading && hasFilters">Prova a modificare i filtri di ricerca.</p>
    </div>

    <nav v-if="filteredBookings.length" class="pbt-pagination" aria-label="Pagine prenotazioni">
      <span>{{ startIndex + 1 }}–{{ Math.min(startIndex + pageSize, filteredBookings.length) }} di {{ filteredBookings.length }} · Pagina {{ currentPage }} di {{ pageCount }}</span>
      <label class="pbt-field pbt-page-size">
        <span>Per pagina</span>
        <select v-model.number="pageSize" aria-label="Prenotazioni per pagina in fondo alla lista">
          <option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
        </select>
      </label>
      <div>
        <button type="button" class="pbt-button staff-button-neutral" :disabled="currentPage === 1" @click="page = currentPage - 1"><span class="material-icons" aria-hidden="true">chevron_left</span>Precedente</button>
        <button type="button" class="pbt-button staff-button-neutral" :disabled="currentPage >= pageCount" @click="page = currentPage + 1">Successiva<span class="material-icons" aria-hidden="true">chevron_right</span></button>
      </div>
    </nav>
  </section>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import PublicBookingAttendance from './PublicBookingAttendance.vue';
import PublicBookingNote from './PublicBookingNote.vue';
import { isCancelledBooking, selectTimelineBookings } from '@/lib/publicBookingsTimeline';
import { whatsAppChatUrl } from '@/lib/whatsapp';

export default {
  name: 'PublicBookingsTimeline',
  components: { PublicBookingAttendance, PublicBookingNote },
  props: {
    bookings: { type: Array, default: () => [] },
    commercialStatuses: { type: Object, default: () => ({}) },
    purchaseInfo: { type: Function, default: null },
    situationInfo: { type: Function, default: null },
    saveNote: { type: Function, required: true },
    attendanceSaving: { type: Object, default: () => ({}) },
    attendancePending: { type: Object, default: () => ({}) },
    attendanceErrors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    historyLoaded: { type: Boolean, default: false },
    canManage: { type: Boolean, default: false },
    actionsDisabled: { type: Boolean, default: false },
  },
  emits: ['visible-bookings', 'attendance-change', 'open-profile', 'follow-up', 'refresh', 'back'],
  setup(props, { emit }) {
    const filters = reactive({ search: '', from: '', to: '', attendance: '', order: 'desc' });
    const page = ref(1);
    const pageSize = ref(25);
    const invalidRange = computed(() => !!(filters.from && filters.to && filters.from > filters.to));
    const filteredBookings = computed(() => selectTimelineBookings(props.bookings, filters));
    const attendedCount = computed(() => filteredBookings.value.filter(booking => booking.participated === true).length);
    const hasFilters = computed(() => !!(filters.search || filters.from || filters.to || filters.attendance));
    const pageCount = computed(() => Math.max(1, Math.ceil(filteredBookings.value.length / pageSize.value)));
    const currentPage = computed(() => Math.min(page.value, pageCount.value));
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value);
    const pagedBookings = computed(() => filteredBookings.value.slice(startIndex.value, startIndex.value + pageSize.value));
    watch(filters, () => { page.value = 1; });
    watch(pageSize, () => { page.value = 1; });
    watch([pagedBookings, () => props.historyLoaded], ([bookings, complete]) => {
      emit('visible-bookings', complete ? bookings : []);
    }, { immediate: true });
    const resetFilters = () => Object.assign(filters, { search: '', from: '', to: '', attendance: '' });
    const formatDate = dateKey => {
      const date = new Date(`${dateKey}T12:00:00`);
      return Number.isNaN(date.getTime()) ? 'Data non indicata' : date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    // Build colors from the complete list, so filtering, sorting and pagination
    // never assign different colors to appointments on the same date.
    const dateDetailsByKey = computed(() => {
      const days = [...new Set(props.bookings.map(booking => booking.dateKey))]
        .filter(day => /^\d{4}-\d{2}-\d{2}$/.test(day || '') && !Number.isNaN(new Date(`${day}T12:00:00`).getTime()))
        .sort((a, b) => b.localeCompare(a));
      return new Map(days.map((day, index) => [day, {
        day: `${day.slice(8, 10)}/${day.slice(5, 7)}`,
        year: day.slice(0, 4),
        tone: `pbt-date--${index % 6}`,
      }]));
    });
    const dateDetails = dateKey => dateDetailsByKey.value.get(dateKey) || { day: 'Data non indicata', year: '', tone: 'pbt-date--unknown' };
    const purchasePending = { label: 'Verifica in corso…', detail: '', tone: 'neutral' };
    const purchaseDetailsById = computed(() => new Map(pagedBookings.value.map(booking => [
      booking.id,
      props.purchaseInfo?.(booking, props.commercialStatuses[booking.id]) || purchasePending,
    ])));
    const purchaseDetails = booking => purchaseDetailsById.value.get(booking.id) || purchasePending;
    const situationPending = { icon: 'hourglass_top', label: 'Verifica in corso', title: 'Controllo situazione commerciale in corso.', className: 'is-loading' };
    const situationById = computed(() => new Map(pagedBookings.value.map(booking => [
      booking.id, props.situationInfo?.(booking, props.commercialStatuses[booking.id]) || situationPending,
    ])));
    const situationDetails = booking => situationById.value.get(booking.id) || situationPending;
    return {
      filters, page, pageSize, invalidRange, filteredBookings, attendedCount, hasFilters, pageCount,
      currentPage, startIndex, pagedBookings, resetFilters, formatDate, purchaseDetails, isCancelledBooking,
      situationDetails, whatsAppChatUrl, dateDetails,
    };
  },
};
</script>

<style scoped src="../../assets/css/public-bookings-timeline.css"></style>
